import { z } from "zod";
import { TeethArchEnum, type TeethArch } from "./teeth.type";
import {
  areTeethAdjacent,
  getArchFromTooth,
  getTeethFromArch,
} from "./teeth.utils";

export const ToothErrors = {
  NON_UNIQUE_TEETH: "NON_UNIQUE_TEETH",
  NON_ADJACENT_TEETH: "NON_ADJACENT_TEETH",
  NON_MULTI_TEETH: "NON_MULTI_TEETH",
  NO_TOOTH_SELECTED: "NO_TOOTH_SELECTED",
  INVALID_TOOTH: "INVALID_TOOTH",
};

export const toothSchema = z.xor(
  [
    z.coerce.number().int().gte(11).lte(18),
    z.coerce.number().int().gte(21).lte(28),
    z.coerce.number().int().gte(31).lte(38),
    z.coerce.number().int().gte(41).lte(48),
  ],
  { error: ToothErrors.INVALID_TOOTH }
);

const hasUniqueTeeth = (teeth: number[]) =>
  new Set(teeth).size === teeth.length;

const hasUniqueArches = (arches: TeethArch[]) =>
  new Set(arches).size === arches.length;

export const teethSchema = z.array(toothSchema).refine(hasUniqueTeeth, {
  error: ToothErrors.NON_UNIQUE_TEETH,
});

export const uniqueTeethSchema = z
  .array(toothSchema)
  .refine(teeth => teeth.length === 1, {
    error: ToothErrors.NON_UNIQUE_TEETH,
  });

export const bulkTeethSchema = z
  .array(toothSchema)
  .min(1, { error: ToothErrors.NO_TOOTH_SELECTED })
  .refine(hasUniqueTeeth, { error: ToothErrors.NON_UNIQUE_TEETH });

export const adjacentTeethSchema = z
  .array(toothSchema)
  .min(2, { error: ToothErrors.NON_MULTI_TEETH })
  .refine(teeth => areTeethAdjacent(teeth), {
    error: ToothErrors.NON_ADJACENT_TEETH,
  });

export const mixedTeethSchema = z.xor(
  [adjacentTeethSchema, uniqueTeethSchema],
  { error: ToothErrors.NON_ADJACENT_TEETH }
);

export const ArchErrors = {
  NO_ARCH_SELECTED: "NO_ARCH_SELECTED",
  INCOMPLETE_ARCH: "INCOMPLETE_ARCH",
};

export const teethArchSchema = z.enum(
  Object.values(TeethArchEnum)
) satisfies z.ZodType<TeethArch>;

export const archesSchema = z
  .array(teethArchSchema)
  .min(1, { error: ArchErrors.NO_ARCH_SELECTED })
  .refine(hasUniqueArches, { error: ArchErrors.NO_ARCH_SELECTED });

/** Teeth of one or two complete arches — the teeth of an arch-scoped product. */
export const archTeethSchema = z.array(toothSchema).refine(
  teeth => {
    const uniqueTeeth = new Set(teeth);
    const arches = [...new Set(teeth.map(getArchFromTooth))].filter(
      (arch): arch is TeethArch => !!arch
    );

    return (
      uniqueTeeth.size === teeth.length &&
      arches.length > 0 &&
      arches.flatMap(getTeethFromArch).length === uniqueTeeth.size
    );
  },
  { error: ArchErrors.INCOMPLETE_ARCH }
);

export const manyTeethSchema = teethSchema.min(1, {
  error: ToothErrors.NO_TOOTH_SELECTED,
});
